import React, { Component, Fragment } from "react";
import { withAppState, getAppState } from "../../state";
import { getSdk } from "../../services/sdkService";
import { config } from "../../config/config";
import {
  PlayerUI,
  ControlsSettings,
  Controls,
  Tracks,
  NextEpisode,
  Filmstrip,
  PauseScreen,
  EpisodesSelectorScreen,
  Video,
  Title,
  Subtitles,
  PlaybackRate,
  withFocusable,
  ModalSettings,
  Loading
} from "../../services/visualLibraryService";
import { PlayerShadow, ButtonSkipIntroStyled } from "./styles/Styled";
import { keyActions, keyHandlers, onAnyKeyPressed } from "./keyHandler";
import {
  playerUiStates,
  playerEvents,
  playbackStates,
  destroyUiHideTimeout,
  destroyPauseScreenTimer,
  changeState,
  onStateEnter,
  startNextEpisodeCountDown,
  clearNextEpisodeCountDown,
  setPlayerLoadTimer,
  destroyPlayerLoadTimer,
  setBufferingTimer,
  destroyBufferingTimer,
  setPlayerUiHideTimeout,
  setPauseScreenTimer,
  showPlayer,
  hidePlayer
} from "./playerStates";
import { getPlayerErrorMessage, getPlayerErrorMessageHeading, sendPlayerErrorReport } from "./playerErrors";
import { extendTrackInfo, updateTracksState, getLocales, getDefaultLocale } from "./tracks";
import { checkDevice, sendMaxDevicesReport } from "./devices";
import { getSeasonsAndEpisodesList, getVideoData, getTrailerVideoData } from "../../utils/mediaDataRetrieval";
import { i18n } from "../../services/i18nService";
import { disabledTracks } from "./tracks";
import { withSubtheme } from "../../styles/themes/withSubtheme";
import { getLogger } from "../../utils/logger";
import { trackEvent, sendUserEvent } from "../../services/analyticsService";
import { resolution } from "../../config/ui/resolutionConfig";
import { getEpisodeLabel } from "../../services/copyService";
import parsePropertiesToMilliseconds from "../../utils/parsePropertiesToMilliseconds";
import { showForceLogoutError } from "../../services/modalsService";
import Logo from "../../components/Logo";
import { ButtonStyled } from "../../components/ModalSettings/styles/Styled";
import { getTVODInitStatus } from "../../utils/tvodUtils";
// custom title heading
import "./style.css";
import * as Sentry from "@sentry/browser";

let Logger;
const PAGE = "player";
const PLAY_FROM_NEXT_EPISODE = "player_next_episode";
const PLAY_FROM_EPISODE_SELECTOR = "player_episode_selector";
const SKIP_INTRO_BUTTON_FOCUS_PATH = "skipIntroButton";
const SKIP_OUTRO_DISMISS_BUTTON_FOCUS_PATH = "skipOutroDismissButton";
const BUTTON_DISMISS_OUTRO_FOCUS_PATH = "buttonDismissOutroFocusPath"; // TODO This value is hardcoded in visual component. Pass as prop
const ButtonFocus = withFocusable(ButtonStyled);
const ButtonSkipIntroFocus = withFocusable(ButtonSkipIntroStyled);
const VideoFocus = withFocusable(Video);
const FilmstripFocus = withFocusable(Filmstrip);

const defaultFocusPath = "video";

const noPauseScreenStates = [
  playerUiStates.FILMSTRIP,
  playerUiStates.TRACKS,
  playerUiStates.EPISODE_SELECTOR,
  playerUiStates.MEDIA_KEYS
];

let sdk = null;
let locales = null;
let nextEpisodeData = null;
let locationState;

class Player extends Component {
  constructor(props) {
    super(props);
    Logger = getLogger();
    sdk = getSdk();
    sdk.playerManager.destroy();
    locationState = this.props.location.state;
    this.state = {
      playbackState: playbackStates.PLAY,
      elapsedTime: 0,
      tentativePosition: null,
      duration: 0,
      activePanel: null,
      filmstripData: null,
      title: locationState.title,
      seriesTitle: locationState.seriesTitle,
      episodeLabel: locationState.episodeLabel,
      description: locationState.description,
      tvSeasonNumber: locationState.tvSeasonNumber,
      tvSeasonEpisodeNumber: locationState.tvSeasonEpisodeNumber,
      currentTime: 0,
      playbackRate: 0,
      nextEpisodeCountDown: config.get().player.nextEpisodeCountDown,
      skipOutroCountDown: config.get().player.skipOutroCountDown,
      playerBuffering: true,
      errorMessage: null,
      errorMessageHeading: null,
      didFirstBuffer: false,
      canShowControls: false,
      episodeSelectorLoading: false,
      playerState: null
    };

    this.config = {
      refCanStartPlayback: null
    };

    this.preferredAudioLanguage = config.get().preferredAudioLanguage;
    this.preferredSubtitlesLanguage = config.get().preferredSubtitlesLanguage;
    this.playbackPercentages = [...config.get().player.playbackPercentages]; // FIXME why are we recreating the array?

    locationState.playFrom !== "cw_home" && this.updateConfig();
    this.bindings();

    // TODO Remove this from the code and use an env variable instead
    this.debug = false;
    this.currentFocus = null;
    this.settingsRef = React.createRef();
    this.filmstrip = null;
    this.errorButtonClose = null;
    this.errorButtonModalPath = "errorCloseButton";
    this.lastFocused = null;
    this.isKeyDownActive = false;
    this.ffInterval = null;
    this.rwInterval = null;
    this.playbackIconName = "";
    this.nextEpisodeTimer = null;
    this.playbackTimes = null;
    this.previousSecond = null;
    this.shouldChangeAudio = false;
    this.shouldGetFilmstrip = true;
    this.didPlayerProgress = false;
    this.didFirstPlaying = false;
    this.didErrorHappen = false;
    this.skipIntroClicked = false;
    this.skipOutroClicked = false;
    this.dismissOutroInterval = null;
    this.isNextEpisodeRequested = false;

    // Callbacks

    this.onInit = async () => {
      Logger.log("On Init event callback function is going to run. Is mounted: " + this.mounted, "Player.onInit");
      Logger.log("Initializing player load timer", "Player.onInit");
      setPlayerLoadTimer.call(this);
      if (locationState.videoType !== "trailer") {
        const preferredLanguage = await sdk.userManager.getPreferences();
        // TODO: Once this preferences needed outside the player it should be fetched along all the Application
        if (preferredLanguage.playback) {
          this.preferredAudioLanguage = preferredLanguage.playback.audio;
          this.preferredSubtitlesLanguage = preferredLanguage.playback.subtitles;
        }
      }
    };

    this.onPlay = async () => {
      Logger.log("this.mounted value is: " + this.mounted, "Player.onPlay");
      if (this.mounted) {
        this.setState({ playbackState: playbackStates.PLAY });
        if (!this.didFirstPlaying) {
          this.didFirstPlaying = true;
          Logger.log("Requesting playerLoadTimer to be destroyed...", "Player.onPlay");
          destroyPlayerLoadTimer.call(this);
        }
      }
    };

    this.onPause = () => {
      if (this.mounted) {
        if (!noPauseScreenStates.includes(this.state.activePanel)) {
          // FIXME: Should use a changeState function in order to avoid race condition issues.
          changeState.call(this, playerUiStates.CONTROLS_PAUSED);
          setPauseScreenTimer.call(this);
        }
        this.setState({
          playbackState: playbackStates.PAUSE,
          currentTime: sdk.playerManager.getCurrentTime()
        });
      }
    };

    this.onStop = () => {
      const { isLive, moduleId, videoType, seriesId } = locationState;
      videoType !== "trailer" &&
        !isLive &&
        this.state.didFirstBuffer &&
        sendUserEvent({
          module_id: moduleId,
          id: this.config.id,
          action: playbackStates.STOP,
          value: this.getPercentPlayed(),
          eventType: "watch",
          ...(videoType === "series" && { parent_id: seriesId })
        });
      this.setState({ playbackState: playbackStates.STOP });
    };

    this.onTimeChange = time => {
      if (this.mounted) {
        const newState = {};
        const resumeTime = (this.config.playbackTime || 0) * 1000; // FIXME: Move to SDK? (didPlayerProgress)
        if (!this.didPlayerProgress && !!time && (!resumeTime || time >= resumeTime)) {
          this.didPlayerProgress = true;
          newState.canShowControls = true;
        }
        newState.elapsedTime = time || this.state.elapsedTime || 0;
        this.setState(newState);
        locationState.videoType !== "trailer" && this.state.duration && this.checkTimeForSendEvent();
        if (locationState.videoType === "series") {
          this.showSkipIntro();
          this.showSkipOutro();
        }
      }
    };

    this.onFinish = async () => {
      const isSerie = locationState.videoType === "series";
      if (this.mounted) {
        let nextEpisode;
        sdk.playerManager.stop();

        if (isSerie && !this.state.nextEpisode) {
          nextEpisode = await this.getNextEpisode();
        }

        !!nextEpisode && this.setState({ nextEpisode });

        if (this.state.nextEpisode && isSerie) {
          changeState.call(this, playerUiStates.NEXT_EPISODE);
          startNextEpisodeCountDown.call(this, this.state.nextEpisode);
        } else {
          sdk.playerManager.destroy();
          this.backHandler();
        }
      }
    };

    this.onSubtitleChange = params => {
      if (this.mounted) {
        this.setState({
          subtitleText: params.text
        });
      }
    };

    this.onBufferStart = () => {
      if (this.mounted && !this.state.playerBuffering && !this.didErrorHappen) {
        this.props.pauseNavigation();
        this.setState({
          playerBuffering: true,
          canShowControls: false
        });
        Logger.log("Initializing buffering timer", "Player");
        setBufferingTimer.call(this);
      }
    };

    this.onBufferEnd = () => {
      if (this.mounted && this.state.playerBuffering) {
        if (locationState.videoType !== "trailer") {
          if (this.shouldChangeAudio && this.state.audioTracks) {
            this.shouldChangeAudio = false;
            this.state.audioTracks.find(track => !!track.selected && sdk.playerManager.setAudioTrack(track));
          }
        }
        const canShowControls = !!(
          this.didPlayerProgress ||
          (this.config.playbackTime && this.state.didFirstBuffer) ||
          (!this.config.playbackTime && !this.state.didFirstBuffer)
        );
        const newState = {
          playerBuffering: false,
          canShowControls,
          didFirstBuffer: true,
          duration: sdk.playerManager.getDuration()
        };
        if (canShowControls) {
          newState.activePanel = playerUiStates.CONTROLS_PLAYING;
        }
        this.setState(newState);

        this.props.resumeNavigation();
        this.updateFocus();

        destroyBufferingTimer.call(this, false);
        setPlayerUiHideTimeout.call(this, false);
      }
    };

    /**
     * Handles the player state changes.
     * @param {String} state - Player state
     */
    this.onPlayerStateChange = async state => {
      this.setState({ playerState: state });
      if (this.mounted) {
        if (this.state.playerState === sdk.playerManager.states.READY) {
          await this.onReadyPlayer();
          if (locationState.videoType !== "trailer") {
            this.shouldChangeAudio =
              this.state.audioTracks &&
              this.state.audioTracks.length > 1 &&
              this.state.audioTracks[0].language !== this.preferredAudioLanguage;
          }
        } else if (this.state.playerState === sdk.playerManager.states.PLAYING) {
          if (this.shouldChangeAudio && !this.state.playerBuffering && this.state.audioTracks) {
            this.shouldChangeAudio = false;
            this.state.audioTracks.find(track => !!track.selected && sdk.playerManager.setAudioTrack(track));
          }
          if (
            this.shouldGetFilmstrip &&
            config.get().featureToggles.filmstrip &&
            locationState.filmstrip &&
            !this.state.filmstripData
          ) {
            this.shouldGetFilmstrip = false;
            sdk.mediaManager
              .getFilmstripData(locationState.filmstrip)
              .then(filmstripData => this.setState({ filmstripData }));
          }
        } else if (this.state.playerState === sdk.playerManager.states.SUBS_READY) {
          locationState.videoType !== "trailer" && this.loadSubtitlesTracks();
        }
      }
    };

    this.onReadyPlayer = async () => {
      const newState = {};
      if (locationState.videoType !== "trailer") {
        if (!this.state.audioTracks) {
          newState.audioTracks = extendTrackInfo(
            await sdk.playerManager.getAvailableAudioTracks(),
            this.preferredAudioLanguage
          );
        }
      }

      if (!this.state.duration) {
        newState.duration = sdk.playerManager.getDuration();
      }

      this.setState(newState, () => {
        Logger.log("Running setState callback function", "Player.onPlay");
        destroyPauseScreenTimer.call(this);
        clearNextEpisodeCountDown.call(this);
        setPlayerUiHideTimeout.call(this);
      });

      this.props.stateActions.hideGlobalModal();
    };
  }

  async getCredits(credits, mediaGuid) {
    return (
      parsePropertiesToMilliseconds((credits && Object.assign({}, credits)) || {}) ||
      (await this.getTimeMarkers(mediaGuid))
    );
  }

  /**
   * Updates config information to send to the player
   */
  async updateConfig() {
    Object.assign(this.config, {
      releasePids: locationState.releasePid,
      releaseUrl: locationState.releaseUrl,
      videoType: locationState.videoType,
      manifest: locationState.manifest,
      playbackTime: locationState["playbackTime"],
      id: locationState.id,
      has4K: locationState.has4K,
      externalSubtitles: locationState.externalSubtitles,
      isLive: locationState.isLive,
      isFirstPlay: getTVODInitStatus(),
      addonContent: locationState.addonContent || "starz",
      onlineContent: locationState.onlineContent || "online",
      contentType: locationState.contentType,
      ...(config.get().featureToggles.skipIntro &&
        locationState.videoType === "series" && {
          credits: await this.getCredits(locationState.credits, locationState.mediaGuid)
        }),
      playbackAnalytics: {
        season: locationState.tvSeasonNumber || 0,
        episode: locationState.tvSeasonEpisodeNumber || 0,
        mediaGuid: locationState.mediaGuid,
        duration: locationState.duration,
        genre: locationState.genre,
        is4KEnabled: this.is4KSessionSelectedQuality() && locationState.has4K,
        playFrom: locationState.playFrom,
        resume: !!locationState["playbackTime"]
      }
    });
    if (locationState.videoType === "series") {
      if (locationState.seriesId) {
        this.config.seriesId = locationState.seriesId;
      }
      this.config.seasonNumber = locationState.tvSeasonNumber || 1;
      this.config.episodeNumber = locationState.tvSeasonEpisodeNumber || 1;
    }
  }

  /**
   * Binds class methods
   */
  bindings() {
    this.playerErrorHandler = this.playerErrorHandler.bind(this);
    this.backHandler = this.backHandler.bind(this);
    this.togglePlayback = this.togglePlayback.bind(this);
    this.playFromBeginning = this.playFromBeginning.bind(this);
    this.showTracksMenu = this.showTracksMenu.bind(this);
    this.selectTrack = this.selectTrack.bind(this);
    this.showEpisodeSelector = this.showEpisodeSelector.bind(this);
    this.playEpisode = this.playEpisode.bind(this);
    this.onUp = this.onUp.bind(this);
    this.onRight = this.onRight.bind(this);
    this.onDown = this.onDown.bind(this);
    this.onLeft = this.onLeft.bind(this);
    this.onEnter = this.onEnter.bind(this);
    this.findAction = this.findAction.bind(this);
    this.updateFocus = this.updateFocus.bind(this);
    this.onVisibilityChange = this.onVisibilityChange.bind(this);
    this.hasThumbnails = this.hasThumbnails.bind(this);
    this.shouldRenderPlaybackRate = this.shouldRenderPlaybackRate.bind(this);
    this.getEpisodesList = this.getEpisodesList.bind(this);
    this.getNextEpisode = this.getNextEpisode.bind(this);
    this.shouldShowLoading = this.shouldShowLoading.bind(this);
    this.trackEpisodeEvent = this.trackEpisodeEvent.bind(this);
    this.getTimeMarkers = this.getTimeMarkers.bind(this);
    this.showSkipIntro = this.showSkipIntro.bind(this);
    this.showSkipOutro = this.showSkipOutro.bind(this);
    this.skipIntroAction = this.skipIntroAction.bind(this);
    this.skipOutroAction = this.skipOutroAction.bind(this);
    this.dismissOutro = this.dismissOutro.bind(this);
  }

  /**
   * Gets the information for the subtitles menu and selects the selected subtitle in the menu
   */
  loadSubtitlesTracks() {
    const newState = {};
    if (!this.state.subtitleTracks) {
      newState.subtitleTracks = extendTrackInfo(
        sdk.playerManager.getAvailableSubtitleTracks(),
        this.preferredSubtitlesLanguage
      );
      for (let i = 0, l = newState.subtitleTracks.length; i < l; i++) {
        const track = newState.subtitleTracks[i];
        if (track.selected) {
          sdk.playerManager.setSubtitleTrack(track);
        }
      }
    }
    this.setState(newState);
  }

  /**
   * Finds and executes the callback function for the given key pressed
   */
  findAction(key) {
    const keyHandlerForState = keyHandlers[this.state.activePanel];
    if (keyHandlerForState && keyHandlerForState[key]) {
      keyHandlerForState[key].call(this);
    }
    if (keyHandlerForState && keyHandlerForState[keyActions.ANY]) {
      keyHandlerForState[keyActions.ANY].call(this, key);
    }
  }

  onUp() {
    this.findAction(keyActions.ARROW_UP);
  }

  onRight() {
    this.findAction(keyActions.ARROW_RIGHT);
  }

  onDown() {
    this.findAction(keyActions.ARROW_DOWN);
  }

  onLeft() {
    this.findAction(keyActions.ARROW_LEFT);
  }

  onEnter() {
    this.findAction(keyActions.OK);
  }

  onBack() {
    const currentState = this.state.playbackState;
    const previousPanel = this.state.activePanel;
    const nextPanel =
      previousPanel !== playerUiStates.TRACKS
        ? playerUiStates.NONE
        : currentState === playbackStates.PLAY
        ? playerUiStates.CONTROLS_PLAYING
        : playerUiStates.CONTROLS_PAUSED;
    const isPlayerUiStateFilmstripsOrMediaKeys =
      previousPanel === playerUiStates.FILMSTRIP || previousPanel === playerUiStates.MEDIA_KEYS;

    if (previousPanel === playerUiStates.NEXT_EPISODE && locationState.videoType === "series") {
      this.trackEpisodeEvent("next episode-cancel");
    }

    if (previousPanel === playerUiStates.TRACKS) {
      this.lastFocused = this.settingsRef.current.getFocused();
    }

    this.setState({ activePanel: nextPanel }, () => {
      if (previousPanel === playerUiStates.PAUSE && currentState === playbackStates.PAUSE) {
        changeState.call(this, playerUiStates.CONTROLS_PAUSED);
        destroyPauseScreenTimer.call(this);
      }
    });
    noPauseScreenStates.includes(previousPanel) && sdk.playerManager.play();
    this.mounted =
      previousPanel !== playerUiStates.NONE &&
      previousPanel !== playerUiStates.NEXT_EPISODE &&
      previousPanel !== playerUiStates.SKIP_INTRO;
    return this.state.errorMessage || (!this.didPlayerProgress && !isPlayerUiStateFilmstripsOrMediaKeys)
      ? false
      : this.mounted;
  }

  /**
   * Callback to execute when toggle the application on background
   */
  onVisibilityChange() {
    if (document.hidden) {
      // pause app
      sdk.playerManager.pause();
      // sdk.playerManager.destroy();
    } else {
      // resume app
      sdk.playerManager.play();
    }
  }

  /**
   * Updates the focus depending on player UI state
   */
  updateFocus() {
    const active = this.state.activePanel;
    switch (active) {
      case playerUiStates.NONE:
        this.currentFocus = defaultFocusPath;
        break;
      case playerUiStates.CONTROLS_PLAYING:
      case playerUiStates.CONTROLS_PAUSED:
        this.currentFocus =
          this.lastFocused || (locationState.isLive ? ControlsSettings.defaultFocusPath : Controls.defaultFocusPath);
        this.lastFocused = null;
        break;
      case playerUiStates.TRACKS:
        this.currentFocus = Tracks.defaultFocusPath;
        break;
      case playerUiStates.NEXT_EPISODE:
        this.currentFocus = NextEpisode.defaultFocusPath;
        break;
      case playerUiStates.EPISODE_SELECTOR:
        this.currentFocus = EpisodesSelectorScreen.defaultFocusPath;
        break;
      case playerUiStates.SKIP_INTRO:
        this.currentFocus = SKIP_INTRO_BUTTON_FOCUS_PATH;
        break;
      case playerUiStates.SKIP_OUTRO:
        // SKIP_OUTRO_DISMISS_BUTTON_FOCUS_PATH this value use to focus dissmiss button on outro
        //now change it to NextEpisode.defaultFocusPath to defaultfocus on episode
        this.currentFocus = NextEpisode.defaultFocusPath;
        break;
      default:
        this.currentFocus = defaultFocusPath;
        break;
    }
    this.props.setFocus(this.currentFocus);
  }

  showErrorModal(message, errorMessageHeading = null) {
    console.log("errorMessageHeading ==>", errorMessageHeading);
    this.props.resumeNavigation();
    this.setState({ errorMessage: message, errorMessageHeading });
    this.errorButtonClose.props.setFocus();
  }

  static getPlayerLog(player) {
    const { playbackState, activePanel, currentTime, playerBuffering, elapsedTime, tentativePosition } = player.state;
    return {
      playbackState,
      activePanel,
      currentTime,
      playerBuffering,
      elapsedTime,
      tentativePosition
    };
  }

  /**
   * After turning off the player load timer to avoid doubling errors, checks if any error code is received to get
   * its customized error message to show in a modal and sends an error report.
   * @param {Object} error - Error data
   */
  playerErrorHandler(error) {
    if (error && error.data && !error.data.silent) {
      const { sendReport = true } = error;
      try {
        Logger.log(`Player emitted an error ${error.code} with data: ${error.data.name}`, "Player.playerErrorHandler");
        destroyPlayerLoadTimer.call(this, false);
        destroyBufferingTimer.call(this, false);
        Logger.log(`Requesting error message for error: ${error && error.code}`, "Player.playerErrorHandler");
        const message = error.code && getPlayerErrorMessage(error.code);
        const messageHeading = error.code && getPlayerErrorMessageHeading(error.code);
        // Next state checking is due to double errors that might come from sdk, such as related with DRM.
        !this.state.errorMessage && this.showErrorModal(message, messageHeading);
        sendReport && sendPlayerErrorReport.call(this, error);
        this.didErrorHappen = true;
      } catch (error) {
        Sentry.captureException(error);
        Logger.log(`Requesting error message for error: ${error && error.code}`, "Player.playerErrorHandler");
        const message = getPlayerErrorMessage(error.code);
        this.showErrorModal(message);
      }
      sdk.playerManager.stop();
      sdk.playerManager.destroy();
    } else {
      Logger.log(`Player ignored an error ${error.code} with data: ${error.data.name}`, "Player.playerErrorHandler");
    }
  }

  /**
   * Request content for playback (only when come to the player from the CW of the Layout)
   */
  async loadContent() {
    const { id, progress, seriesId, moduleId, is4K, contentType, onlineContent } = locationState.videoConfig;
    const videoData = await getVideoData(id, progress, seriesId, moduleId, is4K, contentType, onlineContent);
    sessionStorage && sessionStorage.setItem("seriesPlaying4K", is4K);
    Object.assign(locationState, videoData);
    this.setState({
      title: locationState.title,
      seriesTitle: locationState.seriesTitle,
      episodeLabel: locationState.episodeLabel,
      description: locationState.description,
      tvSeasonNumber: locationState.tvSeasonNumber,
      tvSeasonEpisodeNumber: locationState.tvSeasonEpisodeNumber
    });
    this.updateConfig();
  }

  /**
   * Request Traiiler content for playback ( when come to the player from Hero)
   *
   */
  async loadTrailerContent() {
    const { id, moduleId } = locationState;
    const videoData = await getTrailerVideoData(id, moduleId);
    Object.assign(locationState, { manifest: videoData.manifest, title: videoData.title, duration: 120 });
    this.setState({
      title: locationState.title
    });
    this.updateConfig();
  }

  async componentDidMount() {
    const {
      pauseNavigation,
      onRef,
      applicationState: { supports4K }
    } = this.props;
    showPlayer();
    pauseNavigation();
    this.clearUserMediaCached();
    this.config.refCanStartPlayback = {};
    onRef(this);
    sdk = await getSdk();
    locales = getLocales();
    sdk.playerManager.on(playbackStates.INIT, this.onInit);

    if (locationState.playFrom === "cw_home") {
      await this.loadContent();
    }
    if (locationState.videoType === "trailer" && locationState.moduleType === "hero") {
      /* 
     Make an api call to receive trailer contents data only for hero types . 
    *other trailer data  already recceived it in  detail page.
    */
      await this.loadTrailerContent();
    }
    try {
      const seriesTitle = locationState.seriesId
        ? (await sdk.mediaManager.getTitleText(locationState.seriesId)).title
        : null;

      this.config.playbackAnalytics = {
        ...this.config.playbackAnalytics,
        title: seriesTitle || this.state.title
      };

      const { playbackAnalytics, maxDevices } = config.get().featureToggles;
      !!playbackAnalytics && (await sdk.playerManager.startPlaybackAnalytics(this.config));

      if (maxDevices && locationState.videoType !== "trailer") {
        try {
          const { freeDeviceSlotsExist, deviceAlreadyRegistered } = await checkDevice.call(this);
          console.log("getAppState().forceLogout", getAppState().forceLogout);
          if (!!getAppState().forceLogout) {
            this.props.resumeNavigation();
            await showForceLogoutError.call(this);
            return;
          }
          if (!deviceAlreadyRegistered && !freeDeviceSlotsExist && !getAppState().forceLogout) {
            this.showErrorModal(i18n.get("pages.player.errors.maximumDevicesReached"));
            sendMaxDevicesReport.call(this);
            return;
          }
        } catch (e) {
          Sentry.captureException(e);
          await this.props.stateActions.setForceLogout(e);
          // if Force logout show the popup
          if (!!getAppState().forceLogout) {
            this.props.resumeNavigation();
            await showForceLogoutError.call(this);
            return;
          }
          this.showErrorModal(e.message);
          return;
        }
      }

      // ON event listener network connection (systemManager)
      sdk.systemManager.application.on("visibilitychange", this.onVisibilityChange);

      this.config.initialBitrate =
        locationState.has4K && supports4K
          ? config.get().sdk.initialBitrate4K
          : locationState.videoType === "trailer"
          ? config.get().sdk.initialBitrateTrailer
          : config.get().sdk.initialBitrateHD;

      sdk.playerManager.on(playerEvents.STATE, this.onPlayerStateChange);
      sdk.playerManager.on(playerEvents.ERROR, this.playerErrorHandler);
      sdk.playerManager.on(playerEvents.TIME_CHANGE, this.onTimeChange);
      sdk.playerManager.on(playerEvents.FINISH, this.onFinish);
      sdk.playerManager.on(playerEvents.SUBTITLE_CHANGE, this.onSubtitleChange);
      sdk.playerManager.on(playerEvents.BUFFER_START, this.onBufferStart);
      sdk.playerManager.on(playerEvents.BUFFER_END, this.onBufferEnd);
      sdk.playerManager.on(playbackStates.PLAY, this.onPlay);
      sdk.playerManager.on(playbackStates.PAUSE, this.onPause);
      sdk.playerManager.on(playbackStates.STOP, this.onStop);

      seriesTitle && this.setState({ seriesTitle });
    } catch (e) {
      await this.props.stateActions.setForceLogout(e);
      // if Force logout show the popup
      if (!!getAppState().forceLogout) {
        this.props.resumeNavigation();
        await showForceLogoutError.call(this);
        return;
      }
      return;
    }

    try {
      Logger.log(this.config.manifest, "Manifest");
      console.log("in did mount end");
      console.log("locationState.manifest ==>", locationState.manifest);
      await sdk.playerManager.setup(this.config, locationState.manifest);
    } catch (e) {
      Sentry.captureException(e);
      Logger.log(
        `Requesting error message for error: ${e.code} after failure in playerManager.setup`,
        "Player.componentDidMount"
      );
      await this.props.stateActions.setForceLogout(e);
      // if Force logout show the popup
      if (!!getAppState().forceLogout) {
        this.props.resumeNavigation();
        await showForceLogoutError.call(this);
        return;
      }
      const message = getPlayerErrorMessage(e.code);
      this.showErrorModal(message);
      destroyPlayerLoadTimer.call(this, false);
      destroyBufferingTimer.call(this, false);
      sdk.playerManager.stop();
      sdk.playerManager.destroy();
    }

    this.calculateTimes();
    this.mounted = true;
  }

  componentDidUpdate(prevProps, prevState) {
    if (prevState.activePanel !== this.state.activePanel) {
      Logger.log(Player.getPlayerLog(this), "Player");
      if (onStateEnter[this.state.activePanel]) {
        onStateEnter[this.state.activePanel].call(this);
      }
      this.updateFocus();
    }
  }

  componentWillUnmount() {
    hidePlayer();
    this.mounted = false;
    this.config.refCanStartPlayback = null; // Set to null in order to change the reference in SDK side
    this.props.resumeNavigation();
    // TODO: We might need to place a loader here, since some player errors require more time than others to stop
    // the player (i.e. when coming from a setDrm error).
    destroyPlayerLoadTimer.call(this);
    destroyBufferingTimer.call(this);
    sdk.playerManager.stop();
    sdk.playerManager.destroy();
    destroyUiHideTimeout.call(this);
    destroyPauseScreenTimer.call(this);
    sdk.systemManager.application.off("visibilitychange", this.onVisibilityChange);
    sdk.playerManager.off(playbackStates.INIT, this.onInit);
    sdk.playerManager.off(playbackStates.PLAY, this.onPlay);
    sdk.playerManager.off(playbackStates.PAUSE, this.onPause);
    sdk.playerManager.off(playbackStates.STOP, this.onStop);
    sdk.playerManager.off(playerEvents.TIME_CHANGE, this.onTimeChange);
    sdk.playerManager.off(playerEvents.FINISH, this.onFinish);
    sdk.playerManager.off(playerEvents.SUBTITLE_CHANGE, this.onSubtitleChange);
    sdk.playerManager.off(playerEvents.BUFFER_START, this.onBufferStart);
    sdk.playerManager.off(playerEvents.BUFFER_END, this.onBufferEnd);
    sdk.playerManager.off(playerEvents.ERROR, this.playerErrorHandler);
    sdk.playerManager.off(playerEvents.STATE, this.onPlayerStateChange);
  }

  /**
   * Looks if the current percentage time played is in the percentages array.
   * This array (this.playbackTimes) has the percentages in which the user event will be sent.
   * @param {Number} currentTimeInSeconds
   * @returns {Number} Returns 0 if the index of the percent playing is in array or -1 if not
   */
  getPlaybackTimePercentIndex(currentTimeInSeconds) {
    const playbackTimeIndex = this.playbackTimes.findIndex(item => item > currentTimeInSeconds);

    if (this.playbackTimes.length > 1) {
      return playbackTimeIndex - 1;
    } else if (this.playbackTimes.length === 1) {
      return this.playbackTimes[0] < currentTimeInSeconds ? 0 : -1;
    }
    return -1;
  }

  /**
   * Returns the percent played with two decimals
   * @returns {Number}
   */
  getPercentPlayed() {
    const percent = this.getCurrentTimestamp() / this.state.duration;
    return percent.toFixed(2);
  }

  /**
   * Sends event of percent playback time viewed
   */
  checkTimeForSendEvent() {
    const { isLive, moduleId, videoType, seriesId } = locationState;
    let currentTimeInSeconds = Math.round(this.getCurrentTimestamp() / 1000);
    if (currentTimeInSeconds !== this.previousSecond) {
      this.calculateTimes();
      const foundPlaybackTime = this.getPlaybackTimePercentIndex(currentTimeInSeconds);
      if (!isLive && this.playbackTimes.length > 0 && foundPlaybackTime > -1) {
        sendUserEvent({
          module_id: moduleId,
          id: this.config.id,
          action: "watch",
          value: this.playbackPercentages[foundPlaybackTime],
          ...(videoType === "series" && { parent_id: seriesId })
        });
        this.playbackPercentages.splice(0, foundPlaybackTime + 1);
      }
      this.previousSecond = currentTimeInSeconds;
    }
  }

  /**
   * Calculates percentages of playback time viewed
   * @returns {Number}
   */
  calculateTimes() {
    if (this.state.duration) {
      this.playbackTimes = this.playbackPercentages.map(percentage => {
        return Math.floor(this.state.duration * percentage) / 1000;
      });
    }
  }

  /**
   * Shows skip intro
   */
  showSkipIntro() {
    if (config.get().featureToggles.skipIntro && !this.skipIntroClicked) {
      if (this.state.activePanel === playerUiStates.NONE && this.shouldShowSkipIntro()) {
        changeState.call(this, playerUiStates.SKIP_INTRO);
      } else if (!this.shouldShowSkipIntro() && this.state.activePanel === playerUiStates.SKIP_INTRO) {
        changeState.call(this, playerUiStates.NONE);
      }
    }
  }

  /**
   * Returns true if the skip intro have to be shown. Otherwise returns false
   * @returns {Boolean}
   */
  shouldShowSkipIntro() {
    const { introStartSeconds = 0, introEndSeconds } = this.config.credits;
    if (introEndSeconds) {
      return this.getCurrentTimestamp() >= introStartSeconds && this.getCurrentTimestamp() <= introEndSeconds;
    }
    return false;
  }

  /**
   * Go to the player position in which the intro ends
   */
  skipIntroAction() {
    this.skipIntroClicked = true;
    sdk.playerManager.seek(this.config.credits.introEndSeconds);
  }

  /**
   * Shows skip outro
   */
  async showSkipOutro() {
    if (config.get().featureToggles.skipOutro && !this.skipOutroClicked) {
      if (this.state.activePanel === playerUiStates.NONE && this.shouldShowSkipOutro()) {
        if (!this.isNextEpisodeRequested) {
          this.isNextEpisodeRequested = true;
          this.setState({
            nextEpisode: await this.getNextEpisode()
          });
        }
        if (this.state.nextEpisode) {
          changeState.call(this, playerUiStates.SKIP_OUTRO);
          this.dismissOutroInterval = setInterval(() => this.skipOutroNextEpisode(), 1000);
        }
      }
    }
  }

  /**
   * Returns true if the skip outro have to be shown. Otherwise returns false
   * @returns {Boolean}
   */
  shouldShowSkipOutro() {
    if (this.config.credits) {
      const { outroStartSeconds } = this.config.credits;
      if (outroStartSeconds) {
        return this.getCurrentTimestamp() >= outroStartSeconds && this.dismissOutroInterval === null;
      }
    }
    return false;
  }

  /**
   * Manages the skip outro countdown
   */
  skipOutroNextEpisode() {
    this.setState({
      skipOutroCountDown: this.state.skipOutroCountDown - 1
    });
    if (this.state.skipOutroCountDown < 1 && locationState.videoType === "series") {
      this.skipOutroAction();
    }
  }

  /**
   * Removes the skip outro screen
   */
  dismissOutro() {
    this.skipOutroClicked = true;
    changeState.call(this, playerUiStates.NONE);
    clearInterval(this.dismissOutroInterval);
    this.dismissOutroInterval = null;
  }

  /**
   * Callback for the skip outro poster
   */
  skipOutroAction() {
    this.dismissOutro();
    this.playEpisode(this.state.nextEpisode, true);
  }

  /**
   * Returns time markers from server
   * @returns {Object}
   */
  async getTimeMarkers(mediaGuid) {
    return (await sdk.mediaManager.getTitleTimeMarkers({ mediaGuid })) || {};
  }

  /**
   * Callback for back button
   */
  backHandler() {
    const { location, history } = this.props;
    if (this.state.activePanel === playerUiStates.NEXT_EPISODE && locationState.videoType === "series") {
      this.trackEpisodeEvent("next episode-cancel");
    }

    if (location.state?.from) {
      history.replace(location.referrer || "/home", {
        ...location.state
      });
      return;
    }
    this.props.history.goBack();
  }

  /**
   * Toggles playback state between play and pause
   */
  togglePlayback() {
    if (this.state.playbackState === playbackStates.PLAY) {
      sdk.playerManager.pause();
    } else {
      sdk.playerManager.play();
      setPlayerUiHideTimeout.call(this);
    }
  }

  playFromBeginning() {
    this.setState({ subtitleText: "" });
    sdk.playerManager.pause();
    sdk.playerManager.seek(0);
  }

  showTracksMenu() {
    changeState.call(this, playerUiStates.TRACKS);
  }

  /**
   * Selects audio and subtitle track
   * @param {Object} selectedTrack
   */
  selectTrack(selectedTrack) {
    if (selectedTrack.type === "TEXT") {
      if (sdk.playerManager.getCurrentSubtitleTrack() !== selectedTrack) {
        sdk.playerManager.setSubtitleTrack(selectedTrack);
        sdk.playerManager.updatePreferences({
          subtitles: selectedTrack.language
        });
        this.setState({
          subtitleTracks: updateTracksState(this.state.subtitleTracks, selectedTrack),
          subtitleText: ""
        });
      }
    } else {
      if (sdk.playerManager.getCurrentAudioTrack() !== selectedTrack) {
        sdk.playerManager.setAudioTrack(selectedTrack);
        sdk.playerManager.updatePreferences({ audio: selectedTrack.language });
        this.setState({
          audioTracks: updateTracksState(this.state.audioTracks, selectedTrack)
        });
      }
    }
  }

  shouldShowLoading() {
    return this.state.playerBuffering || this.state.episodeSelectorLoading || !this.state.canShowControls;
  }

  trackEpisodeEvent(action, episode = nextEpisodeData) {
    const episodeLabel = `${locationState.seriesId}-S${episode.tvSeasonNumber}E${episode.tvSeasonEpisodeNumber}`;
    trackEvent(PAGE, action, episodeLabel);
  }

  clearUserMediaCached() {
    this.props.applicationState.userMediaCached = null;
  }

  is4KSessionSelectedQuality() {
    return sessionStorage && JSON.parse(sessionStorage.getItem("seriesPlaying4K"));
  }

  /**
   * Plays new episode, from next episode or episode selector
   * @param {Object} episode Object with the info of the new episode
   * @param {Boolean} isNextEpisodeCountdownFinished Indicates if the countdown for go to the next episode has finished
   */
  async playEpisode(episode, isNextEpisodeCountdownFinished = false) {
    // If episode selected is the same as the one being played, it will exit episode selector and resume playback.
    if (episode.id === this.config.id) {
      changeState.call(this, playerUiStates.CONTROLS_PLAYING);
      return;
    }

    this.setState({
      playerBuffering: true,
      canShowControls: false,
      skipOutroCountDown: config.get().player.skipOutroCountDown
    });

    this.clearUserMediaCached();
    this.didFirstPlaying = false;
    this.didPlayerProgress = false;
    this.shouldGetFilmstrip = true;
    this.skipIntroClicked = false;
    this.skipOutroClicked = false;
    this.isNextEpisodeRequested = false;

    this.props.pauseNavigation();

    if (this.state.activePanel === playerUiStates.NEXT_EPISODE) {
      let eventAction = "next episode-manual";
      if (isNextEpisodeCountdownFinished) {
        eventAction = "next episode-auto";
      }
      this.trackEpisodeEvent(eventAction, episode);
    }

    if (this.state.activePanel === playerUiStates.EPISODE_SELECTOR) {
      this.trackEpisodeEvent("select episode", episode);
    }

    sdk.playerManager.stop();

    this.playbackPercentages = [...config.get().player.playbackPercentages]; // FIXME Why are we recreating the array?

    this.setState({
      episodeSelectorLoading: true,
      didFirstBuffer: false
    });
    const titleFor4K =
      this.is4KSessionSelectedQuality() &&
      !!episode["4kProgramId"] &&
      (await sdk.mediaManager.getTitle(episode.id, { is4K: true }));
    const has4K = titleFor4K && this.props.applicationState.supports4K;
    const media = has4K ? titleFor4K.title4K.mediaContent[0] : episode.mediaContent[0];
    locationState.filmstrip = media.content.filmstrip || null;
    const playbackTime = (episode.progress && episode.progress.playbackTime) || 0;
    const { releasePid, releaseUrl, streamingUrl, duration } = media.content.video;
    const newConfig = {
      releasePids: releasePid,
      releaseUrl: releaseUrl,
      manifest: streamingUrl,
      videoType: "series",
      playbackTime,
      id: episode.id,
      has4K,
      //externalSubtitles: has4K && media.subtitles,
      externalSubtitles: media.content.subtitles,
      seasonNumber: episode.tvSeasonNumber,
      episodeNumber: episode.tvSeasonEpisodeNumber,
      ...(config.get().featureToggles.skipIntro &&
        locationState.videoType === "series" && {
          credits: await this.getCredits(media.credits, media.mediaGuid)
        }),
      playbackAnalytics: {
        newEpisode: true,
        title: this.state.seriesTitle,
        season: episode.tvSeasonNumber || 0,
        episode: episode.tvSeasonEpisodeNumber || 0,
        mediaGuid: media.mediaGuid,
        duration: duration,
        genre: locationState.genre,
        is4KEnabled: this.is4KSessionSelectedQuality() && has4K,
        playFrom:
          (this.state.activePanel === playerUiStates.NEXT_EPISODE && PLAY_FROM_NEXT_EPISODE) ||
          (this.state.activePanel === playerUiStates.EPISODE_SELECTOR && PLAY_FROM_EPISODE_SELECTOR),
        resume: !!playbackTime
      }
    };
    const newState = {
      title: episode.title,
      description: episode.shortDescription,
      elapsedTime: 0,
      duration: null,
      activePanel: playerUiStates.NONE,
      episodeLabel: getEpisodeLabel(episode.tvSeasonNumber, episode.tvSeasonEpisodeNumber),
      tvSeasonNumber: episode.tvSeasonNumber,
      tvSeasonEpisodeNumber: episode.tvSeasonEpisodeNumber,
      episodeSelectorLoading: false,
      subtitleTracks: null,
      subtitleText: "",
      filmstripData: null,
      nextEpisode: null
    };

    Object.assign(this.config, newConfig);
    Logger.log(this.config.manifest, "Manifest");
    this.setState(newState, () => {
      sdk.playerManager.setup(this.config, locationState.manifest);
    });
  }

  /**
   * Opens the episode selector page
   */
  async showEpisodeSelector() {
    if (locationState.seriesId) {
      this.props.pauseNavigation();
      this.setState({ playerBuffering: true });
      onStateEnter[playerUiStates.EPISODE_SELECTOR] && onStateEnter[playerUiStates.EPISODE_SELECTOR].call(this);
      const seriesData = await getSeasonsAndEpisodesList(locationState.seriesId);
      this.setState({ playerBuffering: false, ...seriesData }, () => {
        changeState.call(this, playerUiStates.EPISODE_SELECTOR);
        this.props.resumeNavigation();
      });
    }
  }

  getCurrentTimestamp() {
    const tentativePosition = this.state.tentativePosition;
    // FIXME, shouldn't the SDK work with all milliseconds?
    return tentativePosition || this.state.elapsedTime || this.config.playbackTime * 1000 || 0;
  }

  getSeasonsList() {
    const { seasonsList } = this.state;
    return (
      (locationState.seasonsList && locationState.seasonsList.length && locationState.seasonsList) ||
      (seasonsList && seasonsList.length && seasonsList)
    );
  }

  getEpisodesList() {
    const { episodesList } = this.state;
    return (
      (locationState.episodesList && locationState.episodesList.length && locationState.episodesList) ||
      (episodesList && episodesList.length && episodesList)
    );
  }

  async getNextEpisode() {
    // FIXME: revisit episodes retrieval flow because this might delay too much the appearance of next episode screen.
    if (
      locationState.seriesId &&
      (!this.state.episodesList ||
        (this.state.episodesList && !this.state.episodesList.length) ||
        !locationState.episodesList ||
        (locationState.episodesList && !locationState.episodesList.length))
    ) {
      this.setState({ episodeSelectorLoading: !this.shouldShowSkipOutro() }); // If we're requesting next episode data from the skip outro we haven't to show loading
      const seriesData = await getSeasonsAndEpisodesList(locationState.seriesId);
      this.setState({ episodeSelectorLoading: false, ...seriesData });
    }

    const episodesList = this.getEpisodesList();
    const index = episodesList.findIndex(
      title =>
        this.state.tvSeasonEpisodeNumber === title.tvSeasonEpisodeNumber &&
        this.state.tvSeasonNumber === title.tvSeasonNumber
    );
    if (episodesList[index + 1]) {
      episodesList[index + 1].progress && (episodesList[index + 1].progress["playbackTime"] = 0);
    }
    nextEpisodeData = episodesList[index + 1];
    return episodesList[index + 1];
  }

  shouldRenderPlaybackRate() {
    return (
      (this.state.activePanel === playerUiStates.FILMSTRIP && (this.state.playbackRate > 0 || !this.hasThumbnails())) || // 1. The active panel is Filmstrip and the Playback Rate is bigger than 0 or there is no thumbnails<OR>
      this.state.activePanel === playerUiStates.MEDIA_KEYS // 2. The active panel is MediaKeys
    );
  }

  hasThumbnails() {
    return (
      this.state.filmstripData && this.state.filmstripData.thumbnails && !!this.state.filmstripData.thumbnails.length
    );
  }

  render() {
    const { isLive, videoType } = locationState;
    const durationNoLive = !isLive ? this.state.duration > 0 : true;
    return !!this.state.errorMessage ? (
      <ModalSettings
        headingText={this.state.errorMessageHeading || ""}
        mainText={this.state.errorMessage}
        headerIcon={<div></div>} // remove the Logo from the popup
      >
        <ButtonFocus
          label={i18n.get("global.close").toUpperCase()}
          focusPath="errorBackButton"
          onEnterPress={this.backHandler}
          size="medium"
          spacing="xsmall"
          displayBlock={true}
          key={i18n.get("global.close").toUpperCase()}
          onRef={ref => (this.errorButtonClose = ref)}
        />
      </ModalSettings>
    ) : (
      <PlayerUI onKeyDown={onAnyKeyPressed.bind(this)}>
        {this.shouldShowLoading() && <Loading overlay />}
        {this.debug && <PlayerDebug player={this} />}
        {this.state.activePanel === playerUiStates.TRACKS && (
          <Tracks
            audioTracks={this.state.audioTracks}
            subtitleTracks={this.state.subtitleTracks}
            audioTitle={i18n.get("pages.player.audio")}
            subtitlesTitle={i18n.get("global.subtitles")}
            onTrackSelected={this.selectTrack}
          />
        )}
        {this.state.activePanel === playerUiStates.EPISODE_SELECTOR &&
          !this.shouldShowLoading() &&
          this.getSeasonsList() &&
          this.getEpisodesList() && (
            <EpisodesSelectorScreen
              seasonsList={this.getSeasonsList()}
              episodesList={this.getEpisodesList()}
              seasonSelected={this.state.tvSeasonNumber}
              episodeSelected={this.state.tvSeasonEpisodeNumber}
              onEnterPress={this.playEpisode}
              dataSnUp={""}
              dataSnDown={""}
              fromDetailPage={true}
              MagicRemoteStatus={false}
              showArrows={false}
              direction={i18n.getDirection()}
              currentFocus={this.props.applicationState.currentFocus}
              slideMarginStart={resolution.carousel.slideMarginStart.episodeSelector}
            />
          )}
        {this.state.activePanel === playerUiStates.NEXT_EPISODE && videoType === "series" && (
          <NextEpisode
            className="next-episode"
            focusPath="nextEpisode"
            imageUrl={this.state.nextEpisode.images.screenshot}
            countdownText={i18n.get("pages.player.nextEpisodeWillStartIn", this.state.nextEpisodeCountDown)}
            episodeLabel={getEpisodeLabel(
              this.state.nextEpisode.tvSeasonNumber,
              this.state.nextEpisode.tvSeasonEpisodeNumber
            )}
            contentTitle={this.state.nextEpisode.title}
            nextEpisodeTitle={this.state.nextEpisode}
            onEnterPress={this.playEpisode}
            backHandler={this.backHandler}
            buttonLabel={i18n.get("global.back").toUpperCase()}
            position="center"
            buttonFocusPath="buttonBackNextEpisode"
          />
        )}
        {this.state.activePanel === playerUiStates.SKIP_INTRO && videoType === "series" && (
          <ButtonSkipIntroFocus
            label={i18n.get("pages.player.skipIntro")}
            focusPath={SKIP_INTRO_BUTTON_FOCUS_PATH}
            onEnterPress={this.skipIntroAction}
            size="small"
            onLeft={this.onLeft}
            onRight={this.onRight}
            onUp={this.onUp}
            onDown={this.onDown}
            buttonType="primary"
          />
        )}
        {this.state.activePanel === playerUiStates.SKIP_OUTRO && videoType === "series" && (
          <NextEpisode
            className="next-episode"
            focusPath={SKIP_OUTRO_DISMISS_BUTTON_FOCUS_PATH}
            imageUrl={this.state.nextEpisode.images.screenshot}
            countdownText={i18n.get("pages.player.nextEpisodeWillStartIn", this.state.skipOutroCountDown)}
            episodeLabel={getEpisodeLabel(
              this.state.nextEpisode.tvSeasonNumber,
              this.state.nextEpisode.tvSeasonEpisodeNumber
            )}
            contentTitle={this.state.nextEpisode.title}
            onEnterPress={this.skipOutroAction}
            backHandler={this.dismissOutro}
            buttonLabel={i18n.get("pages.player.dismissButton")}
            position="bottomRight"
            buttonFocusPath={BUTTON_DISMISS_OUTRO_FOCUS_PATH}
          />
        )}
        {[
          playerUiStates.CONTROLS_PLAYING,
          playerUiStates.CONTROLS_PAUSED,
          playerUiStates.TRACKS,
          playerUiStates.MEDIA_KEYS,
          playerUiStates.FILMSTRIP
        ].indexOf(this.state.activePanel) > -1 &&
          this.state.canShowControls &&
          durationNoLive && (
            <PlayerShadow>
              {this.shouldRenderPlaybackRate() && (
                <PlaybackRate
                  rate={this.state.playbackRate || undefined}
                  iconName={this.playbackIconName || undefined}
                  text={!this.hasThumbnails() ? i18n.get("pages.player.pressOkButtonToResume") : undefined}
                />
              )}
              <Controls
                playbackState={this.state.playbackState}
                playbackHandler={this.togglePlayback}
                elapsedTime={!isLive && this.getCurrentTimestamp()}
                duration={!isLive && this.state.duration}
                hidePlayButton={
                  isLive || [playerUiStates.FILMSTRIP, playerUiStates.MEDIA_KEYS].indexOf(this.state.activePanel) > -1
                }
                dataSnUp={`@${ControlsSettings.defaultFocusPath}`}
                onLeft={this.onLeft}
                onRight={this.onRight}
                onDown={this.onDown}
              />
              {this.state.activePanel !== playerUiStates.FILMSTRIP && (
                <Fragment>
                  <Title
                    className="playerTitle"
                    title={this.state.seriesTitle || this.state.title}
                    episodeLabel={this.state.episodeLabel}
                  />
                  <ControlsSettings
                    settingsBackHandler={this.backHandler}
                    settingsPlayBeginningHandler={videoType !== "trailer" && !isLive && this.playFromBeginning}
                    settingsLanguageHandler={videoType !== "trailer" && this.showTracksMenu}
                    settingsEpisodesHandler={videoType === "series" && this.showEpisodeSelector}
                    dataSnDown={`@${Controls.defaultFocusPath}`}
                    dataSnUp={""}
                    ref={this.settingsRef}
                    direction={config.get().locales[i18n.getLocale()].direction}
                    labelBack={i18n.get("global.back")}
                    labelAudioSubtitles={i18n.get("pages.player.settings.audioSubtitles")}
                    labelPlayBeginning={i18n.get("global.playFromBeginning")}
                    labelEpisodes={i18n.get("global.media.episodes")}
                  />
                </Fragment>
              )}
            </PlayerShadow>
          )}
        {this.state.activePanel === playerUiStates.FILMSTRIP &&
          this.state.filmstripData &&
          this.state.filmstripData.thumbnails &&
          this.state.filmstripData.thumbnails.length > 0 &&
          !this.state.playerBuffering && (
            <FilmstripFocus
              focusPath={"filmstrip"}
              thumbnails={this.state.filmstripData.thumbnails}
              initialThumbnail={this.state.currentFilmstripPosition}
              filmstripCarousel={carousel => (this.filmstrip = carousel)}
              direction={i18n.directions.LEFT_TO_RIGHT}
            />
          )}
        {videoType !== "trailer" && !this.state.playerBuffering && this.state.activePanel === playerUiStates.PAUSE && (
          <PauseScreen
            heading={i18n.get("pages.player.youAreWatching")}
            contentTitle={this.state.title}
            seriesTitle={this.state.seriesTitle}
            episodeLabel={this.state.episodeLabel}
            contentDescription={this.state.description}
          />
        )}
        <VideoFocus
          focusPath={defaultFocusPath}
          onUp={this.onUp}
          onRight={this.onRight}
          onDown={this.onDown}
          onLeft={this.onLeft}
          onEnterPress={this.onEnter}
        />
        {this.state.activePanel !== playerUiStates.FILMSTRIP &&
          sdk.playerManager.getCurrentSubtitleTrack() &&
          sdk.playerManager.getCurrentSubtitleTrack().language !== disabledTracks && (
            <Subtitles
              subtitleText={this.state.subtitleText}
              withControls={this.state.activePanel !== playerUiStates.NONE}
              direction={
                locales[sdk.playerManager.getCurrentSubtitleTrack().language].direction ||
                locales[getDefaultLocale()].direction
              }
            />
          )}
      </PlayerUI>
    );
  }
}

export default withAppState(withSubtheme(Player));

/**
 * Component only for debugging purposes.
 * @param player
 * @return {*}
 * @constructor
 */
const PlayerDebug = ({ player }) => {
  return (
    <div
      style={{
        color: "#FFF",
        position: "fixed",
        right: 0,
        padding: "20px",
        fontSize: "2.5rem",
        textShadow: "1px 1px 2px black",
        zIndex: 999
      }}
    >
      <div>activePanel: {player.state.activePanel}</div>
      <div>playbackState: {player.state.playbackState}</div>
    </div>
  );
};
